// src/shaders/skyShader.js

// Simple pseudo-random function (hash)
// Note: This needs to be defined INSIDE the shader string or imported differently.
// For simplicity, let's embed it directly.
/*
float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
*/

export const vertexShader = `
  attribute float size; // Can pass size per point if needed
  // attribute vec3 color; // REMOVED - Handled by THREE.js when vertexColors = true

  uniform float u_time;
  uniform float u_base_size;
  uniform float u_size_attenuation_factor; // Like drei's factor
  uniform float u_sparkle_speed;
  uniform float u_sparkle_intensity;
  uniform float u_density; // Added density uniform
  uniform bool u_is_night; // New uniform to toggle between day and night

  varying vec3 vColor;
  varying float vDistanceFactor; // For fading
  varying float vIsNight; // Changed from bool to float for mobile compatibility
  varying vec3 vPosition; // Pass position to fragment shader for sky gradient

  // Embed hash function directly
  float hash(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }

  void main() {
    // Pass night mode to fragment shader (as float: 1.0=true, 0.0=false)
    vIsNight = u_is_night ? 1.0 : 0.0;
    vPosition = position; // Pass position for sky gradient

    // In day mode, either hide stars completely or make them much fainter
    if (!u_is_night) {
      // Move most stars out of view - keeping only the brightest ones
      float dayVisibilityThreshold = 0.9; // Only very bright stars visible in day
      float starBrightness = hash(position.xy + 0.2); // Different hash for visibility
      
      if (starBrightness < dayVisibilityThreshold) {
        // Move vertex out of view
        gl_Position = vec4(10000.0, 10000.0, 10000.0, 1.0); 
        gl_PointSize = 0.0;
        return;
      }
    }

    // Density check - discard some vertices randomly based on density
    float starHash = hash(position.xy + 0.1); // Use slightly offset hash for density
    if (starHash > u_density) {
      // Move vertex out of view or set size to 0
      gl_Position = vec4(10000.0, 10000.0, 10000.0, 1.0); 
      gl_PointSize = 0.0;
      return; // Stop processing this vertex
    }

    vColor = color; // Pass color to fragment shader

    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    float distance = length(mvPosition.xyz);

    // Size attenuation based on distance
    gl_PointSize = u_base_size * (u_size_attenuation_factor / distance);

    // Sparkle effect
    float sparkleTime = u_time * u_sparkle_speed + starHash * 6.28; // Add hash for phase offset
    float sparkleFactor = 0.5 + 0.5 * sin(sparkleTime); // Oscillate between 0 and 1
    sparkleFactor = pow(sparkleFactor, 5.0); // Sharpen the peak
    float sparkle = 1.0 + sparkleFactor * u_sparkle_intensity * starHash; // Apply intensity, modulated by hash

    gl_PointSize *= sparkle;

    // Make stars slightly smaller in day mode
    if (!u_is_night) {
      gl_PointSize *= 0.7;
    }

    // Clamp point size (optional)
    gl_PointSize = clamp(gl_PointSize, 1.0, 10.0);
    
    vDistanceFactor = smoothstep(0.9, 1.0, 1.0 - distance / 500.0); // Adjust fade range

    gl_Position = projectionMatrix * mvPosition;
  }
`;

export const fragmentShader = `
  varying vec3 vColor;
  varying float vDistanceFactor; // For fading
  varying float vIsNight; // Changed from bool to float for mobile compatibility
  varying vec3 vPosition; // Receive position for sky gradient

  uniform bool u_fade;

  vec3 getSkyColor(vec3 position) {
    // Create a gradient based on the y position (up/down)
    float y = normalize(position).y * 0.5 + 0.5; // Convert to 0-1 range
    
    // Day sky gradient (blue to light blue)
    vec3 skyTop = vec3(0.0, 0.6, 1.0);      // Deeper blue at top
    vec3 skyHorizon = vec3(0.5, 0.8, 1.0);  // Lighter blue at horizon
    
    // Mix colors based on normalized y position
    return mix(skyHorizon, skyTop, y);
  }

  void main() {
    // Simple circle shape for points
    if (length(gl_PointCoord - vec2(0.5)) > 0.5) discard;

    vec3 baseColor = vColor; // Use color attribute
    float alpha = 1.0;

    // Different behavior for night vs day
    if (vIsNight > 0.5) { // Use > 0.5 instead of direct bool comparison
      // Night mode - stars on black sky (transparent background)
      if (u_fade) {
        alpha *= vDistanceFactor; // Apply distance fade
      }
      gl_FragColor = vec4(baseColor, alpha);
    } 
    else {
      // Day mode - blue sky with faint stars
      // Reduce star brightness during day
      alpha *= 0.3; // Much fainter in day mode
      
      if (u_fade) {
        alpha *= vDistanceFactor; // Apply distance fade
      }
      
      // Get sky color based on position
      vec3 skyColor = getSkyColor(vPosition);
      
      // Stars are rendered on top of the sky color
      gl_FragColor = vec4(baseColor, alpha);
    }
  }
`; 